<script setup lang="ts">
/**
 * FormDirtyDialog
 *
 * Inline unsaved-changes confirmation dialog.
 * Rendered as a sibling inside each form dialog's template so that PrimeVue's
 * dynamic z-index management places it above the parent dialog automatically.
 *
 * Usage:
 *   const { confirmState, onConfirm, onCancel, onVisibleUpdate } = useFormDirty();
 *   // in template:
 *   <FormDirtyDialog :visible="confirmState.visible" @confirm="onConfirm" @cancel="onCancel" @update:visible="onVisibleUpdate" />
 */
import { DialogModal } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';

defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  'update:visible': [v: boolean];
  confirm: [];
  cancel: [];
}>();
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="$t('common.discardChanges')"
    max-width="max-w-sm"
    position="center"
    :show-footer="false"
    no-padding
    @update:visible="emit('update:visible', $event)"
  >
    <div class="flex flex-col px-6 pt-4">
      <p class="text-sm text-gray-600 leading-relaxed">
        {{ $t('formDirty.message') }}
      </p>
      <div class="flex justify-end gap-3 border-t border-gray-100 mt-4 py-4">
        <Button
          :label="$t('mother.keepEditing')"
          severity="secondary"
          outlined
          @click="emit('cancel')"
        />
        <Button
          :label="$t('mother.discard')"
          severity="danger"
          @click="emit('confirm')"
        />
      </div>
    </div>
  </DialogModal>
</template>
